import { useDataProvider, useGetIdentity, type Identifier } from "ra-core";
import { useQuery } from "@tanstack/react-query";

import type { CrmDataProvider } from "../providers/supabase/dataProvider";

/** Mirrors the SQL function, which silently cuts the list to 200 ids. */
const MAX_BATCH = 200;

/**
 * Almikia Epic EC2c (TASK-133): the phones of up to 200 team members in ONE
 * `team_member_contacts` call, for an authenticated team member only (a
 * logged-out or role-less identity never fires the request). The React Query
 * key carries sales ids only -- never a phone -- and nothing is logged.
 * Returns a map sales id (as string) -> E.164 phone; people without a visible
 * phone are simply absent.
 */
export const useTeamContacts = (salesIds: Identifier[]) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data: identity } = useGetIdentity();
  const ids = Array.from(new Set(salesIds.map(String)))
    .sort()
    .slice(0, MAX_BATCH);
  const isAuthenticatedMember = identity?.role != null;

  const { data } = useQuery({
    queryKey: ["team_member_contacts", ids],
    queryFn: () => dataProvider.getTeamContacts(ids),
    enabled: isAuthenticatedMember && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const phones: Record<string, string> = {};
  if (isAuthenticatedMember) {
    for (const row of data ?? []) {
      if (row.phone) phones[String(row.sales_id)] = row.phone;
    }
  }
  return { phones, isAuthenticatedMember };
};
